/**
 * Shared constants for CRM field visibility and filtering.
 *
 * These define which property keys, association targets, and entity types
 * are hidden, system-level, or considered "primary". Used by
 * buildDetailFieldList (v2) and overview-edit-section (v2) — imported from
 * one place to avoid drift.
 */

/** System/internal property keys excluded from rendered property lists.
 *  Timestamps are rendered separately via dedicated components. */
export const SYSTEM_FIELDS = new Set([
  'ahoy_created_at',
  'ahoy_updated_at',
  'entity_id',
  'last_event_seq',
  // Per-user dismissal bookkeeping. Declared with value_read_access: all on every
  // event-kind type (meetings, notes, reminders), so without this it would be
  // appended to the Overview rows as a list of raw user UUIDs the moment anyone
  // dismisses the record. It is surfaced as an action, never as a field.
  'ahoy_dismissed_by_users',
]);

/** Display name fields shown in the record header — excluded from overview rows. */
export const DISPLAY_NAME_FIELDS = new Set([
  'ahoy_display_name',
  'ahoy_first_name',
  'ahoy_last_name',
  'ahoy_name',
]);

/** Association target entity types that have dedicated sections/tabs and should
 *  not appear as fields in the Overview or Properties view. */
export const HIDDEN_ASSOCIATION_TARGETS = new Set([
  'ahoy_note',
  // WORKAROUND until #12411: The Tasks section owns a record's tasks, exactly
  // as the notes section owns its notes. (No-op before an org migrates:
  // ahoy_task does not exist in its schema, so nothing targets it.)
  'ahoy_task',
  'ahoy_file',
  'ahoy_meeting',
  'ahoy_booking',
  'ahoy_booking_type',
  'ahoy_research',
  'ahoy_meeting_prep',
]);

// NOTE: there was previously an ENTITY_TYPES_WITH_DEDICATED_OWNER carve-out here
// ({'ahoy_deal'}) that suppressed the generic Owner row on deals in the read +
// edit overview, on the rationale that the deal sub-header and the Owner
// highlight card already render the same ahoy_deal_to_owner data. That premise
// held on the full-page view (whose highlight card carries an edit pencil) but
// NOT in the slideout, whose only remaining Owner rendering is the read-only
// sub-header badge — so removing the row removed the last way to change a deal's
// owner from the slideout. Deals now behave like contacts and companies, which
// never had the carve-out. Do not reintroduce it without first giving the
// slideout an editable Owner affordance.
